import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Form from "@rjsf/core";
import validator from "@rjsf/validator-ajv8";
import axios from "../../api/axios";
import LeftArrowIcon from "../../icons/leftArrow";
import RightArrowIcon from "../../icons/rightArrow";
import Button from "../../components/button";
import PhoneNumberInput from "../../components/phoneNumberInput";
import PasswordInput from "../../components/passwordInput";
import LoadingButton from "../../components/loadingButton";
import ErrorMessage from "../../components/errorMessage";
import formStyles from "../../components/form/style.module.css";
import styles from "./style.module.css";

const schema = {
  type: "object",
  required: ["name", "phone", "email", "password"],
  properties: {
    name: {
      title: "Name",
      type: "string",
    },
    phone: {
      title: "Mobile Number",
      type: "string",
    },
    email: {
      title: "Email Address",
      type: "string",
      format: "email",
    },
    password: {
      title: "Password",
      type: "string",
      minLength: 6,
    },
  },
};

const uiSchema = {
  "ui:classNames": `${formStyles.formInner} ${styles.form_inner}`,
  "ui:rootFieldId": "signup_step1",
  name: {
    "ui:classNames": `${formStyles.form_field} ${formStyles.col_12}`,
    "ui:placeholder": "Enter your name",
  },
  phone: {
    "ui:widget": PhoneNumberInput,
    "ui:classNames": `${formStyles.form_field} ${formStyles.col_12}`,
    "ui:placeholder": "1345 567-23-56",
  },
  email: {
    "ui:classNames": `${formStyles.form_field} ${formStyles.col_12}`,
    "ui:placeholder": "youremail@gmail.com",
  },
  password: {
    "ui:widget": PasswordInput,
    "ui:classNames": `${formStyles.form_field} ${formStyles.col_12}`,
    "ui:placeholder": "••••••••",
  },
  promoCode: {
    "ui:classNames": `${formStyles.form_field} ${formStyles.col_12}`,
    "ui:placeholder": "Enter your promo code",
  },
  "ui:submitButtonOptions": {
    props: {
      className: formStyles.form_btn,
    },
  },
};

const widgets = {
  phone: PhoneNumberInput,
  password: PasswordInput,
};

const Step1 = ({ formData, onFormSubmit, loading, errorMessage }) => {
  const [formInputs, setFormInputs] = useState(schema);
  const navigate = useNavigate();

  const checkPromoCodeFieldEnabled = useCallback(async () => {
    try {
      let url = `auth/show-promo-code`;
      let options = {
        method: "GET",
        url,
        headers: {
          "Content-Type": "application/json",
        },
        withCredentials: true,
      };
      await axios(options)
        .then((response) => {
          if (response.data.status === 1) {
            // Create a deep copy of schema to avoid mutating the original
            const temp = JSON.parse(JSON.stringify(schema));
            temp.properties['promoCode'] = {
              title: "Promo Code (Optional)",
              type: "string",
            };
            setFormInputs(temp);
          } else {
            setFormInputs(schema);
          }
        })
        .catch((err) => {
          console.error('Error checking promo code field:', err);
          setFormInputs(schema); // Fallback to default schema
        });
    } catch (error) {
      console.error('Error checking promo code field:', error);
      setFormInputs(schema); // Fallback to default schema
    }
  }, []);

  useEffect(() => {
    checkPromoCodeFieldEnabled();
  }, [checkPromoCodeFieldEnabled]);

  return (
    <div className={styles.form_outer}>
      <div className={styles.form_suptitle}>Step 1/2</div>
      <div className={styles.form_title}>Enter Details</div>
      <Form
        className={`${formStyles.form}`}
        schema={formInputs}
        uiSchema={uiSchema}
        widgets={widgets}
        formData={formData}
        validator={validator}
        showErrorList={false}
        omitExtraData={true}
        liveValidate={false}
        onSubmit={({ formData }) => {
          onFormSubmit(formData);
        }}
      >
        <div className={styles.form_btn_container}>
          <Button
            title={"Back"}
            onClick={() => navigate(-1)}
            startIcon={<LeftArrowIcon />}
          />
          <LoadingButton
            type="submit"
            loading={loading}
            title={"Next"}
            icon={<RightArrowIcon />}
          />
        </div>
      </Form>
      {errorMessage && <ErrorMessage message={errorMessage} mt />}
    </div>
  );
};

export default Step1;
